// AllBookings.tsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import CollapseHeader from '../../../../core/common/collapse-header';
import { all_routes } from '../../../router/all_routes';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';
import { Button, Modal, Form } from 'react-bootstrap';
import Swal, { SweetAlertResult } from 'sweetalert2';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import type { Booking } from '../booking';
import ViewVehicleBookings from './ViewVehicleBookings';
import { isGatePassSuccess, getGatePassErrorMessage } from '../../../../core/utils/gatePassUtils';

type CurrentUser = {
  empNumber: string;
  roles: unknown[];
  group: string | null;
};

type DriverLite = {
  empNumber: string;
  name: string;
};

type VehicleLite = {
  numberPlate: string;
  model: string;
  noOfPassengers: number;
};

type MarkInForm = {
  vehicleNumber: string;
  inDriverId: string;
  kmsIn: number;
  comment: string;
};

type StatusColors = Record<string, string>;

type AllBookingsProps = {
  statusColors?: StatusColors;
  onView?: (b: Booking) => void;
  onEdit?: (b: Booking) => void;
};

const showCancelConfirmation = (onConfirm: () => void) => {
  const swalWithBootstrapButtons = Swal.mixin({
    customClass: {
      confirmButton: 'btn btn-success swal-confirm-button',
      cancelButton: 'btn btn-danger swal-cancel-button',
      popup: 'small-swal',
    },
    buttonsStyling: false,
  });

  swalWithBootstrapButtons
    .fire({
      title: 'Cancel Confirmation',
      text: 'Do you really want to cancel this booking? This action cannot be reversed.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, cancel it!',
      cancelButtonText: 'No, keep it',
      reverseButtons: true,
    })
    .then((result: SweetAlertResult) => {
      if (result.isConfirmed) {
        onConfirm();
        swalWithBootstrapButtons.fire({
          title: 'Canceled!',
          text: 'The booking has been canceled successfully.',
          icon: 'success',
          customClass: { popup: 'small-swal' },
        });
      } else if (result.isDismissed && result.dismiss === Swal.DismissReason.cancel) {
        swalWithBootstrapButtons.fire({
          title: 'Cancelled',
          text: 'The booking was not canceled.',
          icon: 'error',
          customClass: { popup: 'small-swal' },
        });
      }
    });
};

const showError = (message: string) => {
  Swal.fire({
    icon: 'error',
    title: 'Oops...',
    text: message,
    customClass: { popup: 'small-swal' },
  });
};

if (typeof document !== 'undefined' && !document.getElementById('swal-small-styles')) {
  const style = document.createElement('style');
  style.id = 'swal-small-styles';
  style.textContent = `
    .swal-cancel-button { margin-right: 10px; }
    .small-swal { width: 400px !important; padding: 1rem !important; font-size: 0.9rem !important; }
    .small-swal .swal2-title { font-size: 1.2rem !important; }
    .small-swal .swal2-html-container { font-size: 0.9rem !important; }
    .small-swal .swal2-icon { font-size: 1rem !important; }
    .small-swal .swal2-actions { font-size: 0.8rem !important; }
    .small-swal .btn { padding: 0.3rem 0.8rem !important; font-size: 0.8rem !important; }
  `;
  document.head.appendChild(style);
}

const getCurrentUser = (): CurrentUser => {
  if (typeof window === 'undefined') {
    return { empNumber: 'unknown', roles: [], group: null };
  }
  let roles: unknown[] = [];
  try {
    roles = JSON.parse(localStorage.getItem('roles') || '[]');
  } catch {
    roles = [];
  }
  return {
    empNumber: localStorage.getItem('username') || localStorage.getItem('empNumber') || 'unknown',
    roles,
    group: localStorage.getItem('group') || null,
  };
};

const canUpdateBooking = (booking: Booking | undefined, currentUser: CurrentUser): boolean => {
  if (!booking) return false;
  const isCreator = booking.createdBy === currentUser.empNumber;
  const isServiceManager = !!currentUser.group?.includes('SERVICE_MANAGER');
  const isDepartmentHead = !!currentUser.group?.includes('DEPARTMENT_HEAD');
  const status = (booking.status || 'UNKNOWN').toUpperCase();

  if (['COMPLETED', 'MERGED', 'EXPIRED', 'CANCELLED', 'CANCELED'].includes(status)) return false;
  if (isServiceManager && ['PENDING', 'APPROVED', 'FINAL_APPROVED'].includes(status)) return true;
  if (isDepartmentHead && ['PENDING', 'APPROVED'].includes(status)) return true;
  if (isCreator && status === 'PENDING') return true;
  return false;
};

const isOngoingStatus = (status?: string | null): boolean => {
  const s = (status || '').toUpperCase();
  return ['ONGOING', 'ON_GOING', 'IN_PROGRESS'].includes(s);
};

const canSeeMarkIn = (booking: Booking | undefined, currentUser: CurrentUser): boolean => {
  if (!booking) return false;
  const isServiceManager = !!currentUser.group?.includes('SERVICE_MANAGER');
  return isServiceManager && isOngoingStatus(booking.status || undefined);
};

const STATUS_COLORS: StatusColors = {
  PENDING: 'badge bg-secondary',
  APPROVED: 'badge bg-success',
  ONGOING: 'badge bg-info',
  FINAL_APPROVED_NEW: 'badge bg-info',
  FINAL_REJECTED: 'badge bg-primary',
  FINAL_APPROVED: 'badge bg-success',
  COMPLETED: 'badge bg-success-gradient',
  CANCEL: 'badge bg-danger',
  REJECTED: 'badge bg-danger',
  CANCELLED: 'badge bg-danger',
  MERGED: 'badge bg-teal',
  EXPIRED: 'badge bg-danger',
};

const AllBookings: React.FC<AllBookingsProps> = ({
  statusColors = STATUS_COLORS,
  onEdit = (b) => console.log('edit booking', b),
}) => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<CurrentUser>(getCurrentUser());
  const [showViewModal, setShowViewModal] = useState<boolean>(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalBookings, setTotalBookings] = useState<number>(0);
  const pageSize = 15;
  const totalPages = Math.max(1, Math.ceil((totalBookings || 0) / pageSize));

  // Mark In Modal
  const [showMarkInModal, setShowMarkInModal] = useState<boolean>(false);
  const [markInBookingId, setMarkInBookingId] = useState<number | null>(null);
  const [drivers, setDrivers] = useState<DriverLite[]>([]);
  const [vehicles, setVehicles] = useState<VehicleLite[]>([]);
  const [loadingDrivers, setLoadingDrivers] = useState<boolean>(false);
  const [loadingVehicles, setLoadingVehicles] = useState<boolean>(false);
  const [markInForm, setMarkInForm] = useState<MarkInForm>({
    vehicleNumber: '',
    inDriverId: '',
    kmsIn: 0,
    comment: '',
  });

  const fetchBookings = async (page: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await (apiDefinitions as any).getVehicleBookings(page, {
        filter: {},
        sort: { field: 'createdAt', order: 'ASC' },
      });
      const res = response?.data || {};
      const payload = res?.data || {};
      if (res?.status === 'S1000' && Array.isArray(payload?.vehicleBookingRequests)) {
        setBookings(payload.vehicleBookingRequests as Booking[]);
        setTotalBookings(typeof payload.total === 'number' ? payload.total : payload.vehicleBookingRequests.length || 0);
      } else {
        setBookings([]);
        setTotalBookings(0);
        setError(res?.statusDescription || 'No bookings available.');
      }
    } catch (err: any) {
      setBookings([]);
      setTotalBookings(0);
      setError(err?.response?.data?.statusDescription || `Failed to fetch bookings: ${err?.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentUser(getCurrentUser());
    fetchBookings(1);
  }, []);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      fetchBookings(newPage);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      fetchBookings(newPage);
    }
  };

  const handleViewBooking = (booking: Booking) => {
    setSelectedBooking(booking);
    setShowViewModal(true);
  };

  const navItems1 = [
    { label: 'Booking Dashboard', icon: 'ti ti-dashboard', link: all_routes.booking, permission: 'ROLE_ADD_VEHICLE_BOOKING' },
    { label: 'All Bookings', icon: 'ti ti-plus', link: all_routes.allBookings, permission: 'ROLE_VIEW_ALL_BOOKINGS' },
  ];

  // Cancel booking
  const promptCancelBooking = async (bookingId: number) => {
    const { value: reason, isConfirmed } = await Swal.fire({
      title: 'Reason for Cancellation',
      input: 'text',
      inputLabel: 'Please enter the reason',
      inputPlaceholder: 'Enter reason for cancellation',
      inputValidator: (value) => (!value?.trim() ? 'Reason is required' : undefined),
      showCancelButton: true,
      confirmButtonText: 'Continue',
      cancelButtonText: 'Close',
      customClass: { popup: 'small-swal' },
    });

    if (!isConfirmed || !reason?.trim()) return;

    showCancelConfirmation(async () => {
      try {
        await (apiDefinitions as any).cancelVehicleBookingRequest(reason.trim(), bookingId, null);
        await fetchBookings(currentPage);
      } catch (err: any) {
        const msg = err?.response?.data?.statusDescription || `Failed to cancel booking`;
        showError(msg);
      }
    });
  };

  // MARK IN: Fetch Drivers & Available Vehicles (same as Mark Out)
  const fetchMarkInData = async () => {
    setLoadingDrivers(true);
    setLoadingVehicles(true);

    // Drivers
    try {
      const payload = { filterType: null, filterValue: '', date: null };
      const res = await (apiDefinitions as any).getUsersByType('DRIVER', payload);
      if (res?.data?.status === 'S1000') {
        const list = Array.isArray(res.data.data?.users) ? res.data.data.users : [];
        setDrivers(list.map((u: any) => ({
          empNumber: String(u.empNumber),
          name: u.name || 'Unknown',
        })));
      }
    } catch (e) {
      console.error('Failed to load drivers', e);
    } finally {
      setLoadingDrivers(false);
    }

    // Vehicles - Use /vehicle/get-all
    try {
      const res = await apiDefinitions.getVehicles(1);
      if (res.data.status === 'S1000') {
        const available = (res.data.data || [])
          .filter((v: any) => v.vehicleStatus === 'AVAILABLE')
          .map((v: any) => ({
            numberPlate: v.numberPlate,
            model: v.vehicleModel || 'Unknown',
            noOfPassengers: Number(v.noOfPassengers) || 0,
          }));
        setVehicles(available);
      }
    } catch (e) {
      console.error('Failed to load vehicles', e);
    } finally {
      setLoadingVehicles(false);
    }
  };

  const openMarkInModal = async (booking: Booking) => {
    setMarkInBookingId(booking.id);

    // Pre-fill from booking (real fields)
    const assignedVehicle = booking.vehicle?.numberPlate || '';
    const assignedDriverId = (booking as any).outDriverId || booking.driver?.empNumber || '';

    setMarkInForm({
      vehicleNumber: assignedVehicle,
      inDriverId: assignedDriverId,
      kmsIn: 0,
      comment: '',
    });

    await fetchMarkInData();
    setShowMarkInModal(true);
  };

  const closeMarkInModal = () => {
    setShowMarkInModal(false);
    setMarkInBookingId(null);
    setMarkInForm({ vehicleNumber: '', inDriverId: '', kmsIn: 0, comment: '' });
    setVehicles([]);
  };

  const handleMarkInSubmit = async () => {
    if (!markInBookingId) return;

    if (!markInForm.vehicleNumber || !markInForm.inDriverId || markInForm.kmsIn <= 0 || !markInForm.comment.trim()) {
      showError('Please fill all fields');
      return;
    }

    const payload = {
      vehicleBookingRequestId: markInBookingId,
      vehicleNumber: markInForm.vehicleNumber,
      inDriverId: markInForm.inDriverId,
      kmsIn: markInForm.kmsIn,
      comment: markInForm.comment,
    };

    try {
      const res = await (apiDefinitions as any).markVehicleIn(payload);
      if (isGatePassSuccess(res)) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: `Booking #${markInBookingId} marked in`,
        });
        closeMarkInModal();
        fetchBookings(currentPage);
      } else {
        showError(res?.data?.statusDescription || 'Failed');
      }
    } catch (e: any) {
      showError(getGatePassErrorMessage(e, 'Failed to mark vehicle in'));
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title">
                    <i className="ti ti-list-check" style={{ color: "var(--rm-primary)" }} />
                    <span>All Vehicle Bookings</span>
                  </h4>
                  <div className="text-muted small mt-1">Vehicle Management &bull; Booking &bull; Complete Log</div>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons">
                    <CollapseHeader />
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-3">
              <ul className="nav nav-tabs">
                {navItems1.map((item, index) =>
                  item.permission && checkPermission(item.permission) ? (
                    <li className="nav-item" key={index}>
                      <Link to={item.link} className={`nav-link ${index === 1 ? 'active' : ''}`}>
                        <i className={item.icon}></i> {item.label}
                      </Link>
                    </li>
                  ) : null
                )}
              </ul>
            </div>

            {loading ? (
              <div className="card p-4 text-center text-muted">
                <div className="spinner-border spinner-border-sm text-primary mb-2" role="status" />
                <div>Loading bookings...</div>
              </div>
            ) : error ? (
              <div className="alert alert-danger">{error}</div>
            ) : bookings.length > 0 ? (
              <div className="card p-3">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Emp Num</th>
                        <th>Name</th>
                        <th>Vehicle</th>
                        <th>Status</th>
                        <th>Priority</th>
                        <th>Created At</th>
                        <th style={{ minWidth: 200 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map((booking) => {
                        const statusKey = (booking?.status ?? 'UNKNOWN').toUpperCase();
                        const badgeClass = statusColors[statusKey] ?? 'badge-soft-secondary';
                        return (
                          <tr key={booking.id}>
                            <td className="fw-semibold">#{booking.id}</td>
                            <td>{booking?.user?.empNumber || 'N/A'}</td>
                            <td className="fw-medium">{booking?.user?.name?.split(' ').slice(-2).join(' ') || 'N/A'}</td>
                            <td>{booking?.vehicle?.numberPlate || <span className="text-muted">—</span>}</td>
                            <td>
                              <span className={`badge ${badgeClass}`}>{booking?.status || 'N/A'}</span>
                            </td>
                            <td>
                              <span className="badge-soft-primary px-2 py-1 rounded-2">{booking?.priority || 'N/A'}</span>
                            </td>
                            <td>{booking?.createdAt ? new Date(booking.createdAt).toLocaleDateString() : '—'}</td>
                            <td>
                              <div className="d-flex gap-2 align-items-center flex-wrap">
                                <button
                                  className="btn btn-outline-primary btn-sm rounded-circle d-inline-flex align-items-center justify-content-center"
                                  style={{ width: 34, height: 34 }}
                                  onClick={() => handleViewBooking(booking)}
                                  title="View Booking Details"
                                >
                                  <i className="ti ti-eye" aria-hidden="true" />
                                  <span className="visually-hidden">View</span>
                                </button>

                                {canSeeMarkIn(booking, currentUser) && (
                                  <button
                                    className="btn btn-success btn-sm d-inline-flex align-items-center gap-1"
                                    onClick={() => openMarkInModal(booking)}
                                    title="Mark Vehicle In"
                                  >
                                    <i className="ti ti-check" />
                                    <span>Mark In</span>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-4 pt-3 border-top">
                  <Button
                    variant="outline-primary"
                    className="d-inline-flex align-items-center gap-1 px-3 py-1"
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1 || loading}
                  >
                    <i className="ti ti-chevron-left" />
                    <span>Previous</span>
                  </Button>
                  <div className="badge-soft-primary px-3 py-2 rounded-pill fw-semibold" style={{ fontSize: "0.82rem" }}>
                    Page {currentPage} of {totalPages}
                  </div>
                  <Button
                    variant="outline-primary"
                    className="d-inline-flex align-items-center gap-1 px-3 py-1"
                    onClick={handleNextPage}
                    disabled={currentPage >= totalPages || loading}
                  >
                    <span>Next</span>
                    <i className="ti ti-chevron-right" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="card p-4 text-center text-muted">No bookings available.</div>
            )}
          </div>
        </div>
      </div>

      {/* Mark In Modal */}
      <Modal show={showMarkInModal} onHide={closeMarkInModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Mark Vehicle In {markInBookingId ? `for Booking #${markInBookingId}` : ''}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Vehicle (Currently Assigned)</Form.Label>
              <Form.Select
                value={markInForm.vehicleNumber}
                onChange={(e) => setMarkInForm(prev => ({ ...prev, vehicleNumber: e.target.value }))}
                disabled={loadingVehicles}
              >
                <option value="">Select Vehicle</option>
                {vehicles.map(v => (
                  <option key={v.numberPlate} value={v.numberPlate}>
                    {v.numberPlate} ({v.noOfPassengers} pax) – {v.model}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Driver (Currently Assigned)</Form.Label>
              <Form.Select
                value={markInForm.inDriverId}
                onChange={(e) => setMarkInForm(prev => ({ ...prev, inDriverId: e.target.value }))}
                disabled={loadingDrivers}
              >
                <option value="">Select Driver</option>
                {drivers.map(d => (
                  <option key={d.empNumber} value={d.empNumber}>
                    {d.name} ({d.empNumber})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Kilometers In</Form.Label>
              <Form.Control
                type="number"
                value={markInForm.kmsIn || ''}
                onChange={(e) => setMarkInForm(prev => ({ ...prev, kmsIn: parseInt(e.target.value) || 0 }))}
                placeholder="e.g. 53420"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Comment</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={markInForm.comment}
                onChange={(e) => setMarkInForm(prev => ({ ...prev, comment: e.target.value }))}
                placeholder="e.g. Vehicle returned after trip"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeMarkInModal}>Cancel</Button>
          <Button
            variant="success"
            onClick={handleMarkInSubmit}
            disabled={!markInForm.vehicleNumber || !markInForm.inDriverId || markInForm.kmsIn <= 0 || !markInForm.comment.trim()}
          >
            Confirm Mark In
          </Button>
        </Modal.Footer>
      </Modal>

      <ViewVehicleBookings
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        booking={selectedBooking}
        statusColors={statusColors}
      />
    </div>
  );
};

export default AllBookings;